import produce from "immer";
import { Color } from "@glossy/colors";
import { TimeUnit } from "./cards/types/time-unit";

type Unit = "absoluteValue" | "percent";

type ComparisonPeriod = "last_year" | "previous_period";

export enum DatavizName {
  BubbleChart = "bubble-chart",
  Cloud = "cloud",
  Demographic = "demographic",
  DoubleTimeChart = "double-time-chart",
  Embedded = "embedded",
  HorizontalBarChart = "horizontal-bar-chart",
  HorizontalStackedBarChart = "horizontal-stacked-bar-chart",
  Kpi = "kpi",
  LineChart = "line-chart",
  Map = "map",
  MultipleLineChart = "multiple-line-chart",
  PictureWall = "picture-wall",
  DoughnutChart = "pie-chart", // Don't change the name. The API only recognizes doughnut charts named "pie-chart". We also need to support legacy charts whose dataviz name is "pie-chart".
  ScatterPlot = "scatter-plot",
  StackedBarChart = "stacked-bar-chart",
  StreamChart = "stream-chart",
  Sunburst = "sunburst",
  Text = "text",
  TopAuthors = "top-authors",
  TopPosts = "top-posts",
  TreeMap = "treemap",
  PinnedPost = "pinned-post",
  PinnedAuthor = "pinned-author",
  ImageClustering = "image-clustering",
  Emv = "emv",
  AI = "ai-analysis",
}

export interface DatavizBubbleChart {
  name: DatavizName.BubbleChart;
  scaleX?: "logarithmic" | "linear";
  scaleY?: "logarithmic" | "linear";
}

export interface DatavizCloud {
  name: DatavizName.Cloud;
  color?: string;
  hiddenTerms?: string[];
}

export interface DatavizDemographic {
  name: DatavizName.Demographic;
}

export interface DatavizDoubleTimeChart {
  name: DatavizName.DoubleTimeChart;
  scale?: "logarithmic" | "linear";
  displayVolumes?: boolean;
  timeUnit?: TimeUnit;
}

export interface DatavizEmbedded {
  name: DatavizName.Embedded;
  displayLink?: boolean;
  size?: "fit" | "fill";
  url: string;
}

export interface DatavizHorizontalBarChart {
  name: DatavizName.HorizontalBarChart;
  mainUnit?: "total" | "percentage" | "average";
  secondaryUnit?: "percentage" | "none" | "total" | "average" | "evolution";
  sort?: "alpha" | "mainUnit" | "default";
  order?: "asc" | "desc";
  displayAvatar?: boolean;
  displayNullValues?: boolean;
  color?: string;
  isNarrow?: boolean;
  hiddenTerms?: string[];
}

export interface DatavizHorizontalStackedBarChart {
  name: DatavizName.HorizontalStackedBarChart;
  mainUnit?: "percentage" | "total";
  sort?: "alpha" | "default" | string;
  displayVolumes?: boolean;
  chartValuesFormat?: "absolute" | "relative";
}

export interface DatavizKpi {
  name: DatavizName.Kpi;
  dailyMetrics?: boolean;
  displayDistributions?: boolean;
  color?: string;
}

export interface DatavizLineChart {
  name: DatavizName.LineChart;
  scale?: "logarithmic" | "linear";
  color?: Color | string;
  displayVolumes?: boolean;
  timeUnit?: TimeUnit;
}

export interface DatavizMap {
  name: DatavizName.Map;
  variant?: "greyscale" | "chart" | "heatmap" | "custom";
  color?: string;
  populations?: boolean;
}

export interface DatavizMultipleLineChart {
  name: DatavizName.MultipleLineChart;
  scale?: "logarithmic" | "linear";
  displayVolumes?: boolean;
  legendSort?: "alpha" | "volumeDesc";
  filteredValues?: { value: string; color: string }[];
  timeUnit?: TimeUnit;
}

export interface DatavizPictureWall {
  name: DatavizName.PictureWall;
}

export interface DatavizDoughnutChart {
  name: DatavizName.DoughnutChart;
}

export interface DatavizScatterPlot {
  name: DatavizName.ScatterPlot;
  scaleX?: "logarithmic" | "linear";
  scaleY?: "logarithmic" | "linear";
}

export interface DatavizPinnedPost {
  name: DatavizName.PinnedPost;
  /** Post UID (e.g. "r3d3_4-17951184436857782") */
  postId: string;
}
export interface DatavizPinnedAuthor {
  name: DatavizName.PinnedAuthor;
  /** Author UID (e.g. "r3_prod-tw-12345") */
  authorId: string;
}

export interface DatavizStackedBarChart {
  name: DatavizName.StackedBarChart;
  mainUnit?: Unit;
  secondaryUnit?: Unit;
  sort?: "alpha" | "mainUnit" | "default";
  renderMode?: "gauges";
  renderMax?: "total";
  normalized?: boolean;
  scale?: "linear" | "logarithmic";
  displayVolumes?: boolean;
  timeUnit?: TimeUnit;
}

export interface DatavizStreamChart {
  name: DatavizName.StreamChart;
  displayVolumes?: boolean;
}

export interface DatavizSunburst {
  name: DatavizName.Sunburst;
  nodesLimitByChildren?: number;
  nodesLimitByDepth?: number;
}

export interface DatavizText {
  name: DatavizName.Text;
  content: string;
  displayTitle: boolean;
}

export interface DatavizTopAuthors {
  name: DatavizName.TopAuthors;
}

export interface DatavizTopPosts {
  name: DatavizName.TopPosts;
}

export interface DatavizTreeMap {
  name: DatavizName.TreeMap;
  nodesLimitByChildren?: number;
  nodesLimitByDepth?: number;
}

export interface DatavizImageClustering {
  name: DatavizName.ImageClustering;
}

export interface DatavizEmv {
  name: DatavizName.Emv;
}

export type DatavizTypeAI =
  | "general-analysis"
  | "signal-analysis"
  | "risk-analysis"
  | "audience-analysis";
export interface DatavizAI {
  name: DatavizName.AI;
}

export type Dataviz =
  | DatavizBubbleChart
  | DatavizCloud
  | DatavizDemographic
  | DatavizDoubleTimeChart
  | DatavizEmbedded
  | DatavizHorizontalBarChart
  | DatavizHorizontalStackedBarChart
  | DatavizKpi
  | DatavizLineChart
  | DatavizMap
  | DatavizMultipleLineChart
  | DatavizPictureWall
  | DatavizDoughnutChart
  | DatavizPinnedPost
  | DatavizPinnedAuthor
  | DatavizScatterPlot
  | DatavizStackedBarChart
  | DatavizStreamChart
  | DatavizSunburst
  | DatavizText
  | DatavizTopAuthors
  | DatavizTopPosts
  | DatavizTreeMap
  | DatavizImageClustering
  | DatavizEmv
  | DatavizAI;

export type DatavizEditableColors =
  | DatavizCloud
  | DatavizHorizontalBarChart
  | DatavizKpi
  | DatavizLineChart
  | DatavizMap
  | DatavizMultipleLineChart;

export type DatavizTimeUnit =
  | DatavizLineChart
  | DatavizMultipleLineChart
  | DatavizDoubleTimeChart
  | DatavizStackedBarChart;

export function isDoubleTimeChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizDoubleTimeChart {
  return dataviz.name === DatavizName.DoubleTimeChart;
}

export function isKpiDataviz(dataviz: Dataviz): dataviz is DatavizKpi {
  return dataviz.name === DatavizName.Kpi;
}

export function isLineChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizLineChart {
  return dataviz.name === DatavizName.LineChart;
}

export function isDemographicDataviz(
  dataviz: Dataviz
): dataviz is DatavizDemographic {
  return dataviz.name === DatavizName.Demographic;
}

export function isCloudDataviz(dataviz: Dataviz): dataviz is DatavizCloud {
  return dataviz.name === DatavizName.Cloud;
}

export function isHorizontalBarChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizHorizontalBarChart {
  return dataviz.name === DatavizName.HorizontalBarChart;
}

export function isStreamChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizStreamChart {
  return dataviz.name === DatavizName.StreamChart;
}

export function isDoughnutChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizDoughnutChart {
  return dataviz.name === DatavizName.DoughnutChart;
}

export function isBubbleChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizBubbleChart {
  return dataviz.name === DatavizName.BubbleChart;
}

export function isScatterPlotDataviz(
  dataviz: Dataviz
): dataviz is DatavizScatterPlot {
  return dataviz.name === DatavizName.ScatterPlot;
}

export function isMapDataviz(dataviz: Dataviz): dataviz is DatavizMap {
  return dataviz.name === DatavizName.Map;
}

export function isMultipleLineChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizMultipleLineChart {
  return dataviz.name === DatavizName.MultipleLineChart;
}

export function isStackedBarChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizStackedBarChart {
  return dataviz.name === DatavizName.StackedBarChart;
}

export function isTopAuthorsDataviz(
  dataviz: Dataviz
): dataviz is DatavizTopAuthors {
  return dataviz.name === DatavizName.TopAuthors;
}

export function isTopPostsDataviz(
  dataviz: Dataviz
): dataviz is DatavizTopPosts {
  return dataviz.name === DatavizName.TopPosts;
}

export function isPictureWallDataviz(
  dataviz: Dataviz
): dataviz is DatavizPictureWall {
  return dataviz.name === DatavizName.PictureWall;
}

export function isImageClusteringDataviz(
  dataviz: Dataviz
): dataviz is DatavizImageClustering {
  return dataviz.name === DatavizName.ImageClustering;
}

export function isHorizontalStackedBarChartDataviz(
  dataviz: Dataviz
): dataviz is DatavizHorizontalStackedBarChart {
  return dataviz.name === DatavizName.HorizontalStackedBarChart;
}

export function isSunburstDataviz(
  dataviz: Dataviz
): dataviz is DatavizSunburst {
  return dataviz.name === DatavizName.Sunburst;
}

export function isTreeMapDataviz(dataviz: Dataviz): dataviz is DatavizTreeMap {
  return dataviz.name === DatavizName.TreeMap;
}
export function isDatavizWithEvolutions(dataviz: Dataviz) {
  return (
    isHorizontalBarChartDataviz(dataviz) ||
    isKpiDataviz(dataviz) ||
    isLineChartDataviz(dataviz) ||
    isEmvDataviz(dataviz)
  );
}

export function isEmbeddedDataviz(
  dataviz: Dataviz
): dataviz is DatavizEmbedded {
  return dataviz.name === DatavizName.Embedded;
}

export function isTextDataviz(dataviz: Dataviz): dataviz is DatavizText {
  return dataviz.name === DatavizName.Text;
}

export function isEmvDataviz(dataviz: Dataviz): dataviz is DatavizEmv {
  return dataviz.name === DatavizName.Emv;
}

export function isAIDataviz(dataviz: Dataviz): dataviz is DatavizAI {
  return dataviz.name === DatavizName.AI;
}

export function isPinnedPostDataviz(
  dataviz: Dataviz
): dataviz is DatavizPinnedPost {
  return dataviz.name === DatavizName.PinnedPost;
}

export function isPinnedAuthorDataviz(
  dataviz: Dataviz
): dataviz is DatavizPinnedAuthor {
  return dataviz.name === DatavizName.PinnedAuthor;
}

export function getHeightFromDataviz(dataviz: Dataviz, isSingleLine?: boolean) {
  if (dataviz) {
    if (isSingleLine) {
      switch (dataviz.name) {
        case DatavizName.HorizontalBarChart:
          return 330;
        case DatavizName.ImageClustering:
          return 350;
        case DatavizName.Map:
          return 500;
        case DatavizName.TopAuthors:
          return 330;
        default:
          return 400;
      }
    } else {
      switch (dataviz.name) {
        case DatavizName.HorizontalBarChart:
        case DatavizName.HorizontalStackedBarChart:
        case DatavizName.TopAuthors:
          return 366;
        case DatavizName.ImageClustering:
          return 350;
        case DatavizName.Kpi:
          return 65;
        case DatavizName.Map:
          return 400;
        default:
          return 330;
      }
    }
  } else {
    return 330;
  }
}

type PartialDataviz<T extends Dataviz> = Partial<Omit<T, "name">>;

// eslint-disable-next-line complexity
export function getDefaultDataviz(
  dataviz: Dataviz["name"],
  comparison?: ComparisonPeriod
) {
  switch (dataviz) {
    case DatavizName.HorizontalBarChart:
      return getHorizontalBarChartDataviz(
        comparison
          ? {
              secondaryUnit: "evolution",
            }
          : null
      );
    case DatavizName.DoughnutChart:
      return getDoughnutChartDataviz();
    case DatavizName.TopPosts:
      return getTopPostsDataviz();
    case DatavizName.MultipleLineChart:
      return getMultipleLineChartDataviz();
    case DatavizName.StreamChart:
      return getStreamChartDataviz();
    case DatavizName.DoubleTimeChart:
      return getDoubleTimeChartDataviz();
    case DatavizName.HorizontalStackedBarChart:
      return getHorizontalStackedBarChartDataviz();
    case DatavizName.Cloud:
      return getCloudDataviz();
    case DatavizName.Demographic:
      return getDemographicDataviz();
    case DatavizName.BubbleChart:
      return getBubblechartDataviz();
    case DatavizName.TopAuthors:
      return getTopAuthorsDataviz();
    case DatavizName.PictureWall:
      return getPictureWallDataviz();
    case DatavizName.ImageClustering:
      return getImageClusteringDataviz();
    case DatavizName.Kpi:
      return getKpiDataviz();
    case DatavizName.LineChart:
      return getLineChartDataviz();
    case DatavizName.StackedBarChart:
      return getStackedBarChartDataviz();
    case DatavizName.Map:
      return getMapDataviz();
    case DatavizName.Sunburst:
      return getSunburstDataviz();
    case DatavizName.TreeMap:
      return getTreeMapDataviz();
    case DatavizName.ScatterPlot:
      return getScatterPlotDataviz();
    case DatavizName.Embedded:
      return getEmbeddedDataviz();
    case DatavizName.Text:
      return getTextDataviz();
    case DatavizName.PinnedPost:
      return getPinnedPostDataviz();
    case DatavizName.PinnedAuthor:
      return getPinnedAuthorDataviz();
    case DatavizName.Emv:
      return getEmvDataviz();
    case DatavizName.AI:
      return getAIDataviz();
    default:
      throw new Error();
  }
}

export function updateDatavizOptions<D extends Dataviz, K extends keyof D>(
  dataviz: D,
  name: K,
  value: D[K] | string | Record<string, string>
) {
  if (isHorizontalBarChartDataviz(dataviz) && name === "mainUnit") {
    const has_period_evolution = dataviz.secondaryUnit === "evolution";
    const secondary_unit_value =
      dataviz.secondaryUnit === "evolution"
        ? "evolution"
        : value === "percentage" && !has_period_evolution
        ? "total"
        : "percentage";
    if (secondary_unit_value) {
      return produce(dataviz, (draft) => {
        draft[name as Dataviz["name"]] = value;
        draft["secondaryUnit"] = secondary_unit_value;
      });
    }
  } else if (
    (isCloudDataviz(dataviz) || isHorizontalBarChartDataviz(dataviz)) &&
    name === "hiddenTerms"
  ) {
    return produce(dataviz, (draft) => {
      if (value["status"] === "include") {
        draft.hiddenTerms = dataviz.hiddenTerms
          ? [...dataviz.hiddenTerms, value["id"]]
          : [value["id"]];
      } else {
        draft.hiddenTerms = dataviz.hiddenTerms.filter(
          (term) => term !== value["id"]
        );
      }
    });
  } else {
    return produce(dataviz, (draft) => {
      draft[name as Dataviz["name"]] = value;
    });
  }
}

export function getTopPostsDataviz(
  dataviz?: PartialDataviz<DatavizTopPosts>
): DatavizTopPosts {
  return {
    name: DatavizName.TopPosts,
    ...dataviz,
  };
}

export function getMultipleLineChartDataviz(
  dataviz?: PartialDataviz<DatavizMultipleLineChart>
): DatavizMultipleLineChart {
  return {
    name: DatavizName.MultipleLineChart,
    ...dataviz,
  };
}

export function getDoubleTimeChartDataviz(
  dataviz?: PartialDataviz<DatavizMultipleLineChart>
): DatavizDoubleTimeChart {
  return {
    name: DatavizName.DoubleTimeChart,
    ...dataviz,
  };
}

export function getStreamChartDataviz(
  dataviz?: PartialDataviz<DatavizStreamChart>
): DatavizStreamChart {
  return {
    name: DatavizName.StreamChart,
    ...dataviz,
  };
}

export function getHorizontalBarChartDataviz(
  dataviz?: PartialDataviz<DatavizHorizontalBarChart>
): DatavizHorizontalBarChart {
  return {
    name: DatavizName.HorizontalBarChart,
    ...dataviz,
  };
}

export function getHorizontalStackedBarChartDataviz(
  dataviz?: PartialDataviz<DatavizHorizontalStackedBarChart>
): DatavizHorizontalStackedBarChart {
  return {
    name: DatavizName.HorizontalStackedBarChart,
    ...dataviz,
  };
}

export function getMapDataviz(
  dataviz?: PartialDataviz<DatavizMap>
): DatavizMap {
  return {
    name: DatavizName.Map,
    ...dataviz,
  };
}

export function getDoughnutChartDataviz(
  dataviz?: PartialDataviz<DatavizDoughnutChart>
): DatavizDoughnutChart {
  return {
    name: DatavizName.DoughnutChart,
    ...dataviz,
  };
}

export function getStackedBarChartDataviz(
  dataviz?: PartialDataviz<DatavizStackedBarChart>
): DatavizStackedBarChart {
  return {
    name: DatavizName.StackedBarChart,
    ...dataviz,
  };
}

export function getKpiDataviz(
  dataviz?: PartialDataviz<DatavizKpi>
): DatavizKpi {
  return {
    name: DatavizName.Kpi,
    displayDistributions: true,
    ...dataviz,
  };
}

export function getLineChartDataviz(
  dataviz?: PartialDataviz<DatavizLineChart>
): DatavizLineChart {
  return {
    name: DatavizName.LineChart,
    ...dataviz,
  };
}

export function getCloudDataviz(
  dataviz?: PartialDataviz<DatavizCloud>
): DatavizCloud {
  return {
    name: DatavizName.Cloud,
    ...dataviz,
  };
}

export function getTopAuthorsDataviz(
  dataviz?: PartialDataviz<DatavizTopAuthors>
): DatavizTopAuthors {
  return {
    name: DatavizName.TopAuthors,
    ...dataviz,
  };
}

export function getPictureWallDataviz(
  dataviz?: PartialDataviz<DatavizPictureWall>
): DatavizPictureWall {
  return {
    name: DatavizName.PictureWall,
    ...dataviz,
  };
}

export function getImageClusteringDataviz(
  dataviz?: PartialDataviz<DatavizImageClustering>
): DatavizImageClustering {
  return {
    name: DatavizName.ImageClustering,
    ...dataviz,
  };
}
export function getBubblechartDataviz(
  dataviz?: PartialDataviz<DatavizBubbleChart>
): DatavizBubbleChart {
  return {
    name: DatavizName.BubbleChart,
    ...dataviz,
  };
}

export function getEmvDataviz(
  dataviz?: PartialDataviz<DatavizEmv>
): DatavizEmv {
  return {
    name: DatavizName.Emv,
    ...dataviz,
  };
}

export function getAIDataviz(dataviz?: PartialDataviz<DatavizAI>): DatavizAI {
  return {
    name: DatavizName.AI,
    ...dataviz,
  };
}

export function getScatterPlotDataviz(
  dataviz?: PartialDataviz<DatavizScatterPlot>
): DatavizScatterPlot {
  return {
    name: DatavizName.ScatterPlot,
    ...dataviz,
  };
}

export function getDemographicDataviz(
  dataviz?: PartialDataviz<DatavizDemographic>
): DatavizDemographic {
  return {
    name: DatavizName.Demographic,
    ...dataviz,
  };
}

export function getSunburstDataviz(
  dataviz?: PartialDataviz<DatavizSunburst>
): DatavizSunburst {
  return {
    name: DatavizName.Sunburst,
    ...dataviz,
  };
}

export function getTreeMapDataviz(
  dataviz?: PartialDataviz<DatavizTreeMap>
): DatavizTreeMap {
  return {
    name: DatavizName.TreeMap,
    ...dataviz,
  };
}

export function getEmbeddedDataviz(
  dataviz?: PartialDataviz<DatavizEmbedded>
): DatavizEmbedded {
  return {
    name: DatavizName.Embedded,
    displayLink: false,
    size: "fill",
    url: "",
    ...dataviz,
  };
}

export function getTextDataviz(
  dataviz?: PartialDataviz<DatavizText>
): DatavizText {
  return {
    name: DatavizName.Text,
    content: "",
    displayTitle: true,
    ...dataviz,
  };
}

export function getPinnedPostDataviz(
  dataviz?: PartialDataviz<DatavizPinnedPost>
): DatavizPinnedPost {
  return {
    name: DatavizName.PinnedPost,
    postId: "",
    ...dataviz,
  };
}

export function getPinnedAuthorDataviz(
  dataviz?: PartialDataviz<DatavizPinnedAuthor>
): DatavizPinnedAuthor {
  return {
    name: DatavizName.PinnedAuthor,
    authorId: "",
    ...dataviz,
  };
}

export function isTimeUnitDataviz(dataviz: Dataviz) {
  return (
    isLineChartDataviz(dataviz) ||
    isMultipleLineChartDataviz(dataviz) ||
    isDoubleTimeChartDataviz(dataviz) ||
    isStackedBarChartDataviz(dataviz)
  );
}
